import { CHANNELS } from "../../shared/channels";
import { CalendarEvent, CalendarLink, TaskSnapshot } from "../../shared/types";
import { call, listen } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast, renderList } from "../dom";
import { onViewShown } from "../navigation";
import { flash } from "../tweens";
import { buildMonth, DayLabel, DayMarks, isoDate } from "./calendar_grid";
import { onSharedChange, readSharedAll } from "../shares";
import { locale, t } from "../i18n";

// month view with task due dates, your own events and the events people share with you

let shownMonth = new Date();
let selectedDay = isoDate(new Date());
let events: CalendarEvent[] = [];
let shared: Array<{ name: string; data: CalendarEvent[] }> = [];
let snapshot: TaskSnapshot = { tasks: [], errors: [], updatedAt: "" };
let links: CalendarLink[] = [];

const linkById = (id: string | undefined) => links.find((link) => link.id === id);

interface DayItem {
  text: string;
  kind: "task" | "event" | "shared" | "linked";
  eventId?: string;
  link?: CalendarLink;
}

function marksByDay(): Map<string, DayMarks> {
  const marks = new Map<string, DayMarks>();
  const entryFor = (day: string): DayMarks => {
    const entry = marks.get(day) ?? { tasks: 0, events: 0, linked: [], labels: [] };
    marks.set(day, entry);
    return entry;
  };
  const label = (day: string, item: DayLabel) => entryFor(day).labels!.push(item);
  const mark = (day: string, kind: "tasks" | "events") => { entryFor(day)[kind] += 1; };
  snapshot.tasks.forEach((task) => {
    if (!task.dueDate) return;
    const day = task.dueDate.slice(0, 10);
    mark(day, "tasks");
    label(day, { text: task.title, kind: "task" });
  });
  events.forEach((event) => {
    const link = linkById(event.source);
    const time = event.time || undefined;
    if (!link) {
      mark(event.date, "events");
      label(event.date, { text: event.title, time, kind: "event" });
      return;
    }
    const colors = entryFor(event.date).linked!;
    if (!colors.includes(link.color)) colors.push(link.color);
    label(event.date, { text: event.title, time, kind: "linked", color: link.color });
  });
  shared.forEach((person) => person.data.forEach((event) => {
    mark(event.date, "events");
    label(event.date, { text: `${person.name.toLowerCase()}: ${event.title}`, time: event.time || undefined, kind: "shared" });
  }));
  return marks;
}

function itemsFor(day: string): DayItem[] {
  const tasks = snapshot.tasks.filter((task) => task.dueDate.startsWith(day)).map((task) => ({ text: t("due: {title}", { title: task.title }), kind: "task" as const }));
  const own = events.filter((event) => event.date === day && !event.source).map((event) => ({ text: `${event.time} ${event.title}`.trim(), kind: "event" as const, eventId: event.id }));
  const linked = events.filter((event) => event.date === day && event.source).map((event) => ({
    text: `${event.time}${event.endTime ? `-${event.endTime}` : ""} ${event.title}`.trim(),
    kind: "linked" as const,
    link: linkById(event.source)
  }));
  const theirs = shared.flatMap((person) => person.data.filter((event) => event.date === day).map((event) => ({
    text: `${event.time} ${person.name.toLowerCase()}: ${event.title}`.trim(),
    kind: "shared" as const
  })));
  return [...tasks, ...own, ...linked, ...theirs];
}

function renderDay(): void {
  byId("calendar-day-title").textContent = new Date(`${selectedDay}T12:00`).toLocaleDateString(locale(), { weekday: "long", day: "numeric", month: "long" });
  renderList(byId("calendar-day-items"), itemsFor(selectedDay), (item) => {
    const row = fillFields(cloneTemplate("template-day-item"), { text: item.text });
    row.classList.add(`kind-${item.kind}`);
    if (item.link) {
      row.style.setProperty("--link-color", item.link.color);
      const label = document.createElement("span");
      label.className = "day-item-source";
      label.textContent = item.link.name;
      row.querySelector(".day-item-text")!.append(label);
    }
    const remove = row.querySelector<HTMLButtonElement>('[data-action="remove"]')!;
    remove.hidden = !item.eventId;
    remove.addEventListener("click", async () => {
      await call(CHANNELS.calendarRemove, item.eventId);
      await reload();
    });
    return row;
  });
}

function render(): void {
  byId("calendar-month").textContent = shownMonth.toLocaleDateString(locale(), { month: "long", year: "numeric" });
  byId("calendar-grid").replaceChildren(...buildMonth(shownMonth, marksByDay(), pickDay));
  byId("calendar-grid").querySelector(`[data-date="${selectedDay}"]`)?.classList.add("selected");
  renderDay();
}

function pickDay(day: string): void {
  selectedDay = day;
  render();
}

function moveMonth(step: number): void {
  shownMonth = new Date(shownMonth.getFullYear(), shownMonth.getMonth() + step, 1);
  render();
}

async function reload(): Promise<void> {
  [events, snapshot, shared, links] = await Promise.all([
    call<CalendarEvent[]>(CHANNELS.calendarList),
    // someone without tasks still gets their month
    call<TaskSnapshot>(CHANNELS.tasksList, false).catch(() => ({ tasks: [], errors: [], updatedAt: "" })),
    readSharedAll<CalendarEvent[]>("calendar", []),
    call<CalendarLink[]>(CHANNELS.calendarLinksList).catch(() => [] as CalendarLink[])
  ]);
  render();
  renderLinks();
}

function ago(iso: string): string {
  if (!iso) return t("never synced");
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (minutes < 1) return t("just now");
  if (minutes < 60) return t("{count} min ago", { count: minutes });
  if (minutes < 1440) return t("{count} h ago", { count: Math.round(minutes / 60) });
  return t("{count} d ago", { count: Math.round(minutes / 1440) });
}

function linkRow(link: CalendarLink): HTMLElement {
  const status = link.lastError
    ? `${t(link.lastError)} · ${t("last good sync {when}", { when: ago(link.lastSyncAt) })}`
    : t("synced {when} · {count} events", { when: ago(link.lastSyncAt), count: link.eventCount });
  const row = fillFields(cloneTemplate("template-link-row"), {
    name: link.name, status, chiLabel: t("Let Chi see this calendar"), syncLabel: t("Sync now"), removeLabel: t("Remove")
  });
  row.style.setProperty("--link-color", link.color);
  row.classList.toggle("has-error", Boolean(link.lastError));
  const chi = row.querySelector<HTMLInputElement>('[data-role="chi"]')!;
  chi.checked = link.visibleToChi;
  chi.addEventListener("change", async () => {
    await call(CHANNELS.calendarLinksSetVisible, link.id, chi.checked);
    await reload();
  });
  const sync = row.querySelector<HTMLButtonElement>('[data-role="sync"]')!;
  sync.addEventListener("click", async () => {
    sync.disabled = true;
    sync.textContent = t("Syncing");
    try {
      await call(CHANNELS.calendarLinksSync, link.id);
    } catch (error) {
      flashToast(error instanceof Error ? t(error.message) : t("could not sync"));
    }
    await reload();
  });
  // removing asks twice: the first tap arms the button
  const remove = row.querySelector<HTMLButtonElement>('[data-role="remove"]')!;
  let armed: number | undefined;
  remove.addEventListener("click", async () => {
    if (armed === undefined) {
      remove.textContent = t("Tap again to remove");
      armed = window.setTimeout(() => { armed = undefined; remove.textContent = t("Remove"); }, 3000);
      return;
    }
    window.clearTimeout(armed);
    await call(CHANNELS.calendarLinksRemove, link.id);
    await reload();
  });
  return row;
}

function renderLinks(): void {
  byId("calendar-links-count").textContent = links.length ? String(links.length) : "";
  renderList(byId("calendar-links-list"), links, linkRow);
}

async function addLink(event: Event): Promise<void> {
  event.preventDefault();
  const name = byId<HTMLInputElement>("calendar-links-name");
  const url = byId<HTMLInputElement>("calendar-links-url");
  const button = byId<HTMLButtonElement>("calendar-links-add");
  if (!url.value.trim()) return flash(url);
  button.disabled = true;
  button.textContent = t("Adding");
  try {
    await call(CHANNELS.calendarLinksAdd, name.value, url.value);
    name.value = "";
    url.value = "";
  } catch (error) {
    flashToast(error instanceof Error ? t(error.message) : t("could not add that calendar"));
  }
  button.disabled = false;
  button.textContent = t("Add calendar");
  await reload();
}

async function addEvent(event: Event): Promise<void> {
  event.preventDefault();
  const title = byId<HTMLInputElement>("calendar-add-title");
  if (!title.value.trim()) return flash(title);
  await call(CHANNELS.calendarAdd, selectedDay, byId<HTMLInputElement>("calendar-add-time").value, title.value);
  title.value = "";
  await reload();
}

// monday first, one letter or sign each in the person's language
function paintWeekdays(): void {
  const names = new Intl.DateTimeFormat(locale(), { weekday: "narrow" });
  byId("calendar-weekdays").replaceChildren(...Array.from({ length: 7 }, (_, at) => {
    const cell = document.createElement("span");
    cell.textContent = names.format(new Date(2024, 0, 1 + at));
    return cell;
  }));
}

export function setupCalendarView(): void {
  paintWeekdays();
  byId("calendar-prev").addEventListener("click", () => moveMonth(-1));
  byId("calendar-next").addEventListener("click", () => moveMonth(1));
  byId("calendar-today").addEventListener("click", () => {
    shownMonth = new Date();
    pickDay(isoDate(new Date()));
  });
  byId("calendar-add-form").addEventListener("submit", addEvent);
  byId("calendar-links-form").addEventListener("submit", addLink);
  listen(CHANNELS.calendarUpdated, () => void reload());
  listen(CHANNELS.tasksUpdated, () => void reload());
  onSharedChange("calendar", () => void reload());
  onViewShown("calendar", reload);
}
